import { useEffect, useRef, useState } from "react";
import { Select } from "./Select.tsx";

export interface FilterDef {
  key: string;
  label: string;
  options: { value: string; label: string }[];
}
export interface SortDef {
  value: string;
  label: string;
}

const Check = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

/**
 * Search + multi-select filter chips (each with an "All" option) + a sort
 * control. The parent owns `filters` (key → selected values) and `sort`/`order`,
 * and round-trips every change to the backend so filtering/sorting happens in
 * the query, not the UI.
 */
export function FilterBar({
  search,
  onSearch,
  placeholder = "Search…",
  defs,
  filters,
  onFilters,
  sortDefs,
  sort,
  order,
  onSort,
}: {
  search: string;
  onSearch: (v: string) => void;
  placeholder?: string;
  defs: FilterDef[];
  filters: Record<string, string[]>;
  onFilters: (f: Record<string, string[]>) => void;
  sortDefs: SortDef[];
  sort: string;
  order: "asc" | "desc";
  onSort: (sort: string, order: "asc" | "desc") => void;
}) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openKey && !addOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!barRef.current?.contains(e.target as Node)) {
        setOpenKey(null);
        setAddOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [openKey, addOpen]);

  const defFor = (key: string) => defs.find((d) => d.key === key)!;
  const activeKeys = defs.filter((d) => d.key in filters).map((d) => d.key);
  const available = defs.filter((d) => !(d.key in filters));

  function addKey(key: string) {
    onFilters({ ...filters, [key]: [] });
    setAddOpen(false);
    setOpenKey(key);
  }
  function removeKey(key: string) {
    const next = { ...filters };
    delete next[key];
    onFilters(next);
    if (openKey === key) setOpenKey(null);
  }
  function toggleVal(key: string, val: string) {
    const cur = filters[key] ?? [];
    const next = cur.includes(val) ? cur.filter((v) => v !== val) : [...cur, val];
    onFilters({ ...filters, [key]: next });
  }
  function clearVals(key: string) {
    onFilters({ ...filters, [key]: [] });
  }
  function summary(key: string) {
    const vals = filters[key] ?? [];
    if (!vals.length) return "All";
    const def = defFor(key);
    return vals.map((v) => def.options.find((o) => o.value === v)?.label ?? v).join(", ");
  }

  return (
    <div className="filter-bar" ref={barRef}>
      <div className="search-box">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
        </svg>
        <input className="search-input" value={search} onChange={(e) => onSearch(e.target.value)} placeholder={placeholder} />
        {search && (
          <button className="search-clear" onClick={() => onSearch("")} aria-label="Clear search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="filter-chips">
        {activeKeys.map((key) => {
          const def = defFor(key);
          const vals = filters[key] ?? [];
          return (
            <div className="filter-chip multi" key={key}>
              <button className="fc-trigger" onClick={() => setOpenKey(openKey === key ? null : key)}>
                <span className="fc-label">{def.label}:</span>
                <span className="fc-val">{summary(key)}</span>
                <svg className={`fc-chevron${openKey === key ? " open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <button className="fc-remove" onClick={() => removeKey(key)} aria-label={`Remove ${def.label} filter`}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
              {openKey === key && (
                <div className="ms-menu">
                  <button className={`ms-item${vals.length === 0 ? " active" : ""}`} onClick={() => clearVals(key)}>
                    <span className="ms-check">{vals.length === 0 && <Check />}</span> All
                  </button>
                  <div className="ms-divider" />
                  {def.options.map((o) => (
                    <button key={o.value} className={`ms-item${vals.includes(o.value) ? " active" : ""}`} onClick={() => toggleVal(key, o.value)}>
                      <span className="ms-check">{vals.includes(o.value) && <Check />}</span> {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {available.length > 0 && (
          <div className="add-filter">
            <button className="btn btn-ghost btn-sm" onClick={() => setAddOpen((v) => !v)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Filter
            </button>
            {addOpen && (
              <div className="add-filter-menu">
                {available.map((d) => (
                  <button key={d.key} onClick={() => addKey(d.key)}>{d.label}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="sort-control">
        <span className="sort-label muted">Sort</span>
        <Select value={sort} options={sortDefs} onChange={(v) => onSort(v, order)} ariaLabel="Sort field" />
        <button
          className="btn btn-icon btn-ghost btn-sm"
          onClick={() => onSort(sort, order === "asc" ? "desc" : "asc")}
          aria-label="Toggle sort direction"
          title={order === "asc" ? "Ascending" : "Descending"}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {order === "asc" ? <path d="M12 19V5M5 12l7-7 7 7" /> : <path d="M12 5v14M5 12l7 7 7-7" />}
          </svg>
        </button>
      </div>
    </div>
  );
}
